import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { alpha } from '@mui/material/styles';
import useGetPersonalFields, { PersonalFieldsChannel } from '../../../../hooks/Marketing/useGetPersonalFields';
import ColoredDialogTitle from './creation/shared/ColoredDialogTitle';
import PersonalFieldButtons from './creation/shared/PersonalFieldButtons';

type InsertChannel = 'email' | 'sms' | 'cashdesk';

interface InsertPersonalFieldsDialogProps {
  open: boolean;
  onClose: () => void;
  onInsert: (token: string) => void;
  channel: InsertChannel;
}

const CHANNEL_CONFIG: Record<InsertChannel, { title: string; apiChannel: PersonalFieldsChannel }> = {
  email: { title: 'Insertion de champs personnalisés', apiChannel: 'EMAILING' },
  sms: { title: 'Champs de Personnalisation', apiChannel: 'SMS' },
  cashdesk: { title: 'Champs de Personnalisation', apiChannel: 'CASHDESK' },
};

function InsertPersonalFieldsDialog({ open, onClose, onInsert, channel }: InsertPersonalFieldsDialogProps) {
  const { title, apiChannel } = CHANNEL_CONFIG[channel];
  const { data: categories, loading, error } = useGetPersonalFields(apiChannel);
  const [selectedCategory, setSelectedCategory] = useState('');

  const categoryNames = Object.keys(categories);
  const activeCategory = selectedCategory || categoryNames[0] || '';

  const handleClose = () => {
    setSelectedCategory('');
    onClose();
  };

  const handleInsert = (anchor: string) => {
    onInsert(anchor);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
      <ColoredDialogTitle onClose={handleClose}>{title}</ColoredDialogTitle>

      <DialogContent sx={{ px: 3, '&.MuiDialogContent-root': { paddingTop: '24px' } }}>
        {channel === 'sms' && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, p: 1.5, mb: 2.5, borderRadius: 1.5, backgroundColor: 'badge.blue.bg', border: '1px solid', borderColor: theme => alpha(theme.palette.badge.blue.fg, 0.3) }}>
            <InfoOutlinedIcon sx={{ fontSize: '1rem', color: 'badge.blue.fg', mt: 0.15, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'badge.blue.fg', lineHeight: 1.5 }}>
              Le nombre de SMS envoyés peut varier en fonction de l&apos;utilisation de champs de personnalisation.
            </Typography>
          </Box>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 2, fontSize: '0.82rem', borderRadius: 1.5 }}>{error}</Alert>}

        {!loading && !error && (
          <>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: 'primary.main' } }}>Sélectionnez une catégorie</InputLabel>
              <Select
                value={activeCategory}
                label="Sélectionnez une catégorie"
                onChange={e => setSelectedCategory(e.target.value)}
                MenuProps={{ PaperProps: { sx: { maxHeight: 240 } } }}
                sx={{ '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' }, borderRadius: 1.5 }}
              >
                {categoryNames.map(category => <MenuItem key={category} value={category}>{category}</MenuItem>)}
              </Select>
            </FormControl>

            {activeCategory && <PersonalFieldButtons fields={categories[activeCategory] ?? []} onSelect={handleInsert} />}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<CloseIcon fontSize="small" />}
          onClick={handleClose}
          sx={{ textTransform: 'none', fontSize: '0.82rem', color: 'text.secondary', borderColor: 'divider', borderRadius: 1.5, '&:hover': { borderColor: 'grey.500', backgroundColor: 'grey.50' } }}
        >
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InsertPersonalFieldsDialog;
